import type { OrgUnit } from '../types'

export type OrgUnitTreeNode = OrgUnit & { children?: OrgUnitTreeNode[] }

/**
 * Roll up a flat list of `OrgUnit`s (linked by `parent`) into a hierarchical
 * tree sorted alphabetically at each level. Roots are units with `parent == null`.
 */
export const buildOrgTree = (units: OrgUnit[]): OrgUnitTreeNode[] => {
  const byParent = new Map<number | null, OrgUnit[]>()
  for (const u of units) {
    const key = u.parent ?? null
    const arr = byParent.get(key) ?? []
    arr.push(u)
    byParent.set(key, arr)
  }
  for (const arr of byParent.values()) {
    arr.sort((a, b) => a.name.localeCompare(b.name))
  }

  const attach = (u: OrgUnit): OrgUnitTreeNode => {
    const kids = byParent.get(u.id)
    if (kids && kids.length > 0) {
      return { ...u, children: kids.map(attach) }
    }
    return { ...u }
  }

  return (byParent.get(null) ?? []).map(attach)
}

/**
 * Keep only the nodes that match the predicate OR have a matching descendant.
 * Hierarchical context is preserved so a matching team still shows the
 * division/department it sits under instead of becoming a flat list.
 */
export const pruneOrgTree = (
  nodes: OrgUnitTreeNode[],
  matches: (n: OrgUnitTreeNode) => boolean
): OrgUnitTreeNode[] => {
  const out: OrgUnitTreeNode[] = []
  for (const n of nodes) {
    const filteredKids = n.children ? pruneOrgTree(n.children, matches) : []
    if (matches(n) || filteredKids.length > 0) {
      out.push({
        ...n,
        children: filteredKids.length > 0 ? filteredKids : undefined
      })
    }
  }
  return out
}

export const collectOrgTreeIds = (nodes: OrgUnitTreeNode[]): number[] => {
  const ids: number[] = []
  const walk = (n: OrgUnitTreeNode) => {
    ids.push(n.id)
    n.children?.forEach(walk)
  }
  nodes.forEach(walk)
  return ids
}

/**
 * Map from a unit id to its direct-children count, derived from the flat list.
 * Independent of any filtering applied to the rendered tree, so call sites can
 * always show the real number of children even when search has pruned some.
 */
export const buildChildCountMap = (
  units: OrgUnit[]
): Record<number, number> => {
  const counts: Record<number, number> = {}
  for (const u of units) {
    if (u.parent != null) {
      counts[u.parent] = (counts[u.parent] ?? 0) + 1
    }
  }
  return counts
}
